import { formatCurrency } from '@/utils/currencyUtils';
import { MonetaryAmount } from '@/types';
import { Flex, Typography, theme } from 'antd';

import subcategories from '@/data/campaignTypes.json';

interface TooltipPayload {
  name: string;
  total: MonetaryAmount;
  impressions: number;
  percentage: number;
}

interface CampaignTypesTooltipContentProps {
  payload: TooltipPayload | null;
}

/**
 * Content component for chart tooltips displaying value and currency information
 */
export const CampaignTypesTooltipContentEcho = ({ payload }: CampaignTypesTooltipContentProps) => {
  const { token } = theme.useToken();

  if (!payload) return null;

  const { name, total } = payload;

  const description =
    (subcategories as Record<string, Array<string>>)[name.toLowerCase().replace(/\s+/g, '_')] || '';

  /**
   * Return the percentage value with no decimals if it rounds to 1 or greater, or with 1 decimal place if less than 1
   *
   * @param value - The raw value.
   * @returns the correctly rounded value.
   */
  const formatValue = (value: number): number => {
    if (!value) {
      return 0;
    }

    if (value < 1) {
      return parseFloat(value.toFixed(1));
    }

    return Math.round(value);
  };

  const accentColor = token.colorPrimary;
  const subtleColor = '#646464';

  return (
    <Flex vertical gap={token.marginXS} style={{ padding: token.paddingXS, width: 200 }}>
      <Typography.Text
        strong
        style={{ textTransform: 'capitalize', fontSize: token.fontSizeSM, marginInlineEnd: token.marginMD }}
      >
        {name}
      </Typography.Text>

      <Flex vertical>
        <Typography.Text strong style={{ color: accentColor }}>
          {formatCurrency(total?.amount || 0, total?.currency)}
        </Typography.Text>
        <Typography.Text style={{ fontSize: token.fontSizeSM, color: subtleColor }}>
          estimated earnings
        </Typography.Text>
      </Flex>

      <Flex vertical>
        <Typography.Text strong style={{ color: accentColor }}>
          {formatValue(payload.percentage)}%
        </Typography.Text>
        <Typography.Text style={{ fontSize: token.fontSizeSM, color: subtleColor }}>
          of total
        </Typography.Text>
      </Flex>

      {/* Filtering out ads and programmatic as they do not have subcategories and their descriptions are used in the campaign types modal table. */}
      {name.toLowerCase() !== 'ads' &&
        name.toLowerCase() !== 'programmatic' &&
        description.length > 0 && (
          <Flex vertical style={{ fontSize: token.fontSizeXL - 6, color: subtleColor }}>
            <Typography.Text style={{ fontSize: token.fontSizeSM - 2, color: subtleColor }}>
              Can include:
            </Typography.Text>
            <ul style={{ margin: 0, paddingInlineStart: token.paddingMD, listStyleType: 'disc' }}>
              {description.map((subcat) => (
                <li key={subcat}>
                  <Typography.Text style={{ fontSize: token.fontSizeSM - 2, color: subtleColor }}>
                    {subcat}
                  </Typography.Text>
                </li>
              ))}
            </ul>
          </Flex>
        )}
    </Flex>
  );
};
